// Production + transit and operations deadlines for /shipping. All dates are UTC calendar
// days so server and client render the same values.

// Carrier transit in business days, from the carriers' published service standards:
// USPS Priority Mail 2-3, FedEx 2Day 2,
// FedEx International Priority / International Connect Plus 1-7 depending on country.
// Standard is longer than USPS Ground Advantage's 2-5 because untracked DHL and USPS
// shipments often arrive on business day 12 (operations, PBP-245).
export const TRANSIT = {
  usps: { standard: [2, 8], express: [2, 3] },
  // Posters, rails and family history books ship FedEx 2Day on both methods.
  fedex: { standard: [2, 2], express: [2, 2] },
  international: [2, 7],
};

export const SHIPPING_METHODS = ['standard', 'express', 'international'];

// Sunny's 2026 suggestions in PBP-245, selected for publication by Petr.
// These are estimates pending the carrier cutoff and lab closure checks.
const HOLIDAY_DEADLINES = {
  2026: {
    standardProducts: { standard: '12-09', express: '12-11', international: '12-03' },
    photoBooks: { standard: '12-08', express: '12-10', international: '12-02' },
    posters: { standard: '12-14', express: '12-16', international: '12-08' },
    familyHistoryBooks: { standard: '12-03', express: '12-07', international: '11-25' },
  },
};

const HOLIDAY_SEASON_START = { month: 9, day: 1 }; // Oct 1
const HOLIDAY_SEASON_END = { month: 11, day: 25 }; // Dec 25, exclusive

const day = (year, month, date) => new Date(Date.UTC(year, month, date));

// Production days come from the products API (`production_days` in the SKU manifest).
// Every SKU of a row shares the same production, so one representative SKU is enough.
// A row whose SKU is missing or has no value is left out.
export const withProduction = (rows, skuProducts) =>
  rows.flatMap((row) => {
    const productionDays = skuProducts.find((product) => product.id === row.sku)?.productionDays;
    if (!productionDays) return [];
    return [{ ...row, production: productionDays.normal, peak: productionDays.peak }];
  });

// A row can override the carrier transit per method when operations quote product-specific times.
export const transitDays = (category, method) =>
  category.transit?.[method] ??
  (method === 'international' ? TRANSIT.international : TRANSIT[category.carrier][method]);

// Business days from order to delivery at normal volume.
export const deliveryDays = (category, method) => {
  const transit = transitDays(category, method);
  return [category.production[0] + transit[0], category.production[1] + transit[1]];
};

// Callers use holidaySeasonYear to select a year with an explicit operations schedule.
export const holidayOrderBy = (category, method, year) =>
  new Date(`${year}-${HOLIDAY_DEADLINES[year][category.holidayGroup][method]}T00:00:00Z`);

// Earliest Standard estimate across categories, for contiguous US delivery.
export const holidaySafeDate = (categories, year) =>
  new Date(
    Math.min(...categories.map((category) => holidayOrderBy(category, 'standard', year).getTime())),
  );

// Categories that share a holidayGroup share their order-by dates, so they are listed together,
// earliest Standard date first.
export const holidayGroups = (categories, year) => {
  const groups = new Map();
  for (const category of categories) {
    const group = groups.get(category.holidayGroup) ?? [];
    groups.set(category.holidayGroup, [...group, category]);
  }

  return [...groups.entries()]
    .map(([holidayGroup, members]) => ({
      holidayGroup,
      categories: members,
      dates: Object.fromEntries(
        SHIPPING_METHODS.map((method) => [method, holidayOrderBy(members[0], method, year)]),
      ),
    }))
    .sort((a, b) => a.dates.standard - b.dates.standard);
};

// Longest delivery across all methods, rounded up to an even number for a 2-day tick scale.
export const deliveryScaleMax = (categories) => {
  const longest = Math.max(
    ...categories.flatMap((category) =>
      SHIPPING_METHODS.map((method) => deliveryDays(category, method)[1]),
    ),
  );
  return Math.ceil(longest / 2) * 2;
};

// No automatic rollover: each year needs its own operations schedule before dates appear.
export const holidaySeasonYear = (today) => {
  const year = today.getUTCFullYear();
  if (!HOLIDAY_DEADLINES[year]) return null;

  const start = day(year, HOLIDAY_SEASON_START.month, HOLIDAY_SEASON_START.day);
  const end = day(year, HOLIDAY_SEASON_END.month, HOLIDAY_SEASON_END.day);

  return today >= start && today < end ? year : null;
};

export const formatDayRange = ([min, max]) => (min === max ? `${min}` : `${min}–${max}`);

export const formatShortDate = (date) =>
  date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
